import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { Box, Collapse, IconButton, ListItemText, styled } from '@mui/material';

import { Checkbox, Typography } from '@zendesk/forethought-elements';

interface SourceFilterOptionProps {
  expandedValues: string[];
  onChange: (value: string[]) => void;
  setExpandedValues: (value: string[]) => void;
  sourceFilterOptions: Array<{ label: string; value: string }>;
  value: string[];
}

const SourceFilterOption = ({
  expandedValues,
  onChange,
  setExpandedValues,
  sourceFilterOptions,
  value,
}: SourceFilterOptionProps) => {
  const { t } = useTranslation('discover');
  const [isCheckboxClicked, setIsCheckboxClicked] = useState(false);

  // Defensive checks for array props
  const safeExpandedValues = Array.isArray(expandedValues)
    ? expandedValues
    : [];
  const safeSourceFilterOptions = Array.isArray(sourceFilterOptions)
    ? sourceFilterOptions
    : [];
  const safeValue = Array.isArray(value) ? value : [];

  const isExpanded = safeExpandedValues.includes('source');
  const childOptions = safeSourceFilterOptions.filter(opt => opt?.value);
  const childValues = childOptions.map(opt => opt.value);

  // Parent is checked if all children are selected
  const isParentChecked =
    childValues.length > 0 && childValues.every(v => safeValue.includes(v));
  // Parent is indeterminate if some (but not all) children are selected
  const isParentIndeterminate =
    !isParentChecked && childValues.some(v => safeValue.includes(v));

  const handleParentClick = () => {
    if (!isCheckboxClicked) {
      if (isExpanded) {
        setExpandedValues(safeExpandedValues.filter(v => v !== 'source'));
      } else {
        setExpandedValues([...safeExpandedValues, 'source']);
      }
    }
    setIsCheckboxClicked(false);
  };

  const handleParentCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    e.stopPropagation();
    setIsCheckboxClicked(true);

    // Expand if not already expanded
    if (!isExpanded) {
      setExpandedValues([...safeExpandedValues, 'source']);
    }

    if (isParentChecked) {
      // Uncheck all children
      onChange(safeValue.filter(v => !childValues.includes(v)));
    } else {
      // Check all children
      onChange([
        ...safeValue.filter(v => !childValues.includes(v)),
        ...childValues,
      ]);
    }
  };

  const handleChildCheckboxChange = (childValue: string) => {
    if (safeValue.includes(childValue)) {
      onChange(safeValue.filter(v => v !== childValue));
    } else {
      onChange([...safeValue, childValue]);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <MenuItem key='source' onClick={handleParentClick}>
        <Checkbox
          aria-label={t('sourceFilterOption.source')}
          checked={isParentChecked}
          indeterminate={isParentIndeterminate}
          onChange={handleParentCheckboxChange}
          padding='4px 8px'
        />
        <ListItemText
          primary={
            <Typography noWrap variant='font14'>
              {t('sourceFilterOption.source')}
            </Typography>
          }
          sx={{
            '&::-webkit-scrollbar': { display: 'none' },
            '-ms-overflow-style': 'none',
            overflowX: 'auto',
            'scrollbar-width': 'none',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        />
        <IconButton
          onClick={e => {
            e.stopPropagation();
            handleParentClick();
          }}
          sx={{
            transform: `rotate(${isExpanded ? '180' : '0'}deg)`,
          }}
        >
          <KeyboardArrowDown />
        </IconButton>
      </MenuItem>
      <Collapse in={isExpanded}>
        <Box
          sx={{
            marginLeft: 2,
          }}
        >
          {childOptions.map(option => (
            <MenuItem
              key={`source-${option.value}`}
              onClick={() => handleChildCheckboxChange(option.value)}
            >
              <Checkbox
                aria-label={option.label}
                checked={safeValue.includes(option.value)}
                onChange={() => handleChildCheckboxChange(option.value)}
                padding='4px 8px'
              />
              <ListItemText
                primary={
                  <Typography noWrap variant='font14'>
                    {option.label}
                  </Typography>
                }
                sx={{
                  '&::-webkit-scrollbar': { display: 'none' },
                  '-ms-overflow-style': 'none',
                  overflowX: 'auto',
                  'scrollbar-width': 'none',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              />
            </MenuItem>
          ))}
        </Box>
      </Collapse>
      <BorderLine className='outer-menu-item' />
    </Box>
  );
};

const MenuItem = styled('div')`
  height: 28px;
  display: flex;
  align-items: center;
  margin: 4px 0;
  &:hover {
    background-color: ${props => props.theme.palette.primary.light};
    cursor: pointer;
  }
`;

const BorderLine = styled('div')`
  &.outer-menu-item {
    margin: 4px 0;
    border-top: 1px solid ${props => props.theme.palette.colors.grey[300]};
  }
`;

export default SourceFilterOption;
